import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { detectBrowserLanguage, isValidLanguage } from '../utils/languageUtils';

// ----------------------------------------------------------------------

/**
 * LanguageDetector Component
 *
 * Detects user's preferred language and redirects to appropriate language-prefixed URL
 * Used for the root "/" path to handle initial language selection
 *
 * Detection priority:
 * 1. localStorage 'preferredLanguage' (language the user picked)
 * 2. Authenticated user's preferred_language field (via backend API)
 * 3. CloudFlare geolocation (CF-IPCountry header via backend API)
 * 4. Browser language (navigator.language)
 * 5. Fallback to Spanish
 *
 * @component
 * @returns {null} This component doesn't render anything
 */
export default function LanguageDetector() {
  const navigate = useNavigate();
  const [isDetecting, setIsDetecting] = useState(true);

  useEffect(() => {
    const detectLanguage = async () => {
      try {
        // Priority 1: the language the user picked. 'i18nextLng' is not used:
        // it holds the language of the last page seen, not a choice.
        const storedLang = localStorage.getItem('preferredLanguage');
        if (storedLang && isValidLanguage(storedLang)) {
          navigate(`/${storedLang}/`, { replace: true });
          setIsDetecting(false);
          return;
        }

        // Priority 3: Call backend API for geolocation-based detection
        // This will also return authenticated user's preference if logged in
        try {
          const response = await axios.get('/api/v1/user/detect_language/');
          const { language, source } = response.data;

          if (language && isValidLanguage(language)) {
            // Store in localStorage for future visits
            localStorage.setItem('preferredLanguage', language);
            navigate(`/${language}/`, { replace: true });
            setIsDetecting(false);
            return;
          }
        } catch (apiError) {
          // Geolocation API failed, falling back to browser detection
        }

        // Priority 4: Fallback to browser language detection
        const browserLang = detectBrowserLanguage();
        localStorage.setItem('preferredLanguage', browserLang);
        navigate(`/${browserLang}/`, { replace: true });
        setIsDetecting(false);
      } catch (error) {
        // Final fallback to Spanish
        navigate('/es/', { replace: true });
        setIsDetecting(false);
      }
    };

    detectLanguage();
  }, [navigate]);

  // This component doesn't render anything - it just handles the redirect
  // Could optionally show a loading spinner while detecting
  return null;
}
